import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, Field, Input } from '@hvac/ui';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

/**
 * Sign-in form.
 *
 * The reference implementation every future form in this product follows:
 * a Zod schema is the single definition of what is valid, React Hook Form
 * drives state, and `Field` supplies the label/error/ARIA wiring. No component
 * hand-rolls validation.
 */

const loginSchema = z.object({
  email: z.email({ message: 'Enter a valid email address.' }),
  password: z.string().min(1, { message: 'Enter your password.' }),
});

export type LoginValues = z.infer<typeof loginSchema>;

export interface LoginFormProps {
  // Declared as a property with a function type rather than a method shorthand:
  // method shorthand is bivariant and carries an implicit `this`, which is
  // wrong for a callback prop that will be destructured and called standalone.
  readonly onSubmit: (values: LoginValues) => Promise<void>;
}

export function LoginForm({ onSubmit }: LoginFormProps) {
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    // Validate on blur rather than on every keystroke: flagging "invalid email"
    // while someone is still typing the third character is noise.
    mode: 'onBlur',
    defaultValues: { email: '', password: '' },
  });

  const submit = handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      await onSubmit(values);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Could not sign in.');
    }
  });

  return (
    <form
      // `handleSubmit` returns a promise. React's onSubmit expects void, and an
      // unhandled rejection here would be swallowed silently, `void` makes the
      // discard explicit while the catch inside `submit` does the real handling.
      onSubmit={(event) => {
        void submit(event);
      }}
      noValidate
      className="flex flex-col gap-4"
    >
      {submitError === null ? null : (
        <Alert tone="danger" title="Could not sign in">
          {submitError}
        </Alert>
      )}

      <Field label="Email" required {...(errors.email ? { error: errors.email.message } : {})}>
        {({ inputProps }) => (
          <Input
            {...inputProps}
            {...register('email')}
            type="email"
            autoComplete="username"
            hasError={errors.email !== undefined}
          />
        )}
      </Field>

      <Field
        label="Password"
        required
        {...(errors.password ? { error: errors.password.message } : {})}
      >
        {({ inputProps }) => (
          <Input
            {...inputProps}
            {...register('password')}
            type="password"
            autoComplete="current-password"
            hasError={errors.password !== undefined}
          />
        )}
      </Field>

      <Button type="submit" isLoading={isSubmitting} size="lg" className="mt-2">
        Sign in
      </Button>
    </form>
  );
}
